/**
 * @fileoverview Chat input component for sending messages
 * @module ctl-chat-input
 * @version 1.0.0
 * 
 * @description
 * A chat input field with send button for composing and sending messages.
 * 
 * @example
 * <ctl-chat-input placeholder="Type a message..."></ctl-chat-input>
 * 
 * @fires message-send - Dispatched when a message is sent with detail: { text }
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-chat-input-bg: var(--ctl-color-surface, var(--ctl-color-white));
    --ctl-chat-input-border: var(--ctl-color-border, var(--ctl-color-border-2));
    --ctl-chat-input-text: var(--ctl-color-text);
    --ctl-chat-button-bg: var(--ctl-color-brand);
    --ctl-chat-button-hover: var(--ctl-color-brand-hover);
    --ctl-chat-button-text: var(--ctl-color-on-brand);
    --ctl-chat-input-padding: var(--ctl-space-4);
    --ctl-chat-input-gap: var(--ctl-space-2);
    --ctl-chat-input-radius: var(--ctl-radius-md, var(--ctl-radius-md));

    display: flex;
    padding: var(--ctl-chat-input-padding);
    border-top: var(--ctl-space-0-5) solid var(--ctl-chat-input-border);
    background: var(--ctl-chat-input-bg);
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
  }

  .input-wrapper {
    display: flex;
    flex: 1;
    gap: var(--ctl-chat-input-gap);
    align-items: center;
  }

  input {
    flex: 1;
    padding: var(--ctl-space-2);
    font-size: var(--ctl-font-size-base);
    border: var(--ctl-space-0-5) solid var(--ctl-chat-input-border);
    border-radius: var(--ctl-chat-input-radius);
    color: var(--ctl-chat-input-text);
    background: var(--ctl-chat-input-bg);
    min-height: var(--ctl-touch-target);
  }

  input:focus {
    outline: var(--ctl-space-0-5) solid var(--ctl-chat-button-bg);
    outline-offset: var(--ctl-space-0-5);
  }

  button {
    padding: var(--ctl-space-2) var(--ctl-space-4);
    background: var(--ctl-chat-button-bg);
    color: var(--ctl-chat-button-text);
    border: none;
    border-radius: var(--ctl-chat-input-radius);
    cursor: pointer;
    font-weight: var(--ctl-font-weight-semibold);
    font-size: var(--ctl-font-size-base);
    transition: background var(--ctl-transition-fast) ease;
    min-height: var(--ctl-touch-target);
    min-width: var(--ctl-touch-target);
  }

  button:hover {
    background: var(--ctl-chat-button-hover);
  }

  button:focus-visible {
    outline: var(--ctl-space-0-5) solid var(--ctl-chat-button-bg);
    outline-offset: var(--ctl-space-0-5);
  }

  button:disabled {
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }
</style>

<div class="input-wrapper" part="wrapper">
  <input type="text" id="input" part="input" />
  <button type="button" id="send" part="button">Send</button>
</div>
`;

/**
 * Chat Input Web Component
 * 
 * @class CtlChatInput
 * @extends HTMLElement
 * 
 * @attr {string} placeholder - Placeholder text for the input field
 * @attr {boolean} disabled - Disables the input and button
 * 
 * @fires message-send - Dispatched when message is sent. Event detail includes {text}.
 * 
 * @csspart wrapper - The wrapper div containing input and button
 * @csspart input - The text input element
 * @csspart button - The send button element
 * 
 * @cssprop --ctl-chat-input-bg - Background color (default: var(--ctl-color-surface))
 * @cssprop --ctl-chat-input-border - Border color (default: var(--ctl-color-border))
 * @cssprop --ctl-chat-input-text - Text color (default: var(--ctl-color-text))
 * @cssprop --ctl-chat-button-bg - Button background color (default: var(--ctl-color-brand))
 * @cssprop --ctl-chat-button-hover - Button hover color (default: var(--ctl-color-brand-hover))
 * @cssprop --ctl-chat-button-text - Button text color (default: var(--ctl-color-on-brand))
 */
export class CtlChatInput extends HTMLElement {
  static get observedAttributes() {
    return ["placeholder", "disabled"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    
    this._input = this.shadowRoot.getElementById("input");
    this._button = this.shadowRoot.getElementById("send");
    
    this._onButtonClick = this._onButtonClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  connectedCallback() {
    this._button.addEventListener("click", this._onButtonClick);
    this._input.addEventListener("keydown", this._onKeyDown);
    this._syncAttributes();
  }

  disconnectedCallback() {
    this._button.removeEventListener("click", this._onButtonClick);
    this._input.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback() {
    this._syncAttributes();
  }

  _syncAttributes() {
    const placeholder = this.getAttribute("placeholder") || "Type a message...";
    this._input.placeholder = placeholder;

    const disabled = this.hasAttribute("disabled");
    this._input.disabled = disabled;
    this._button.disabled = disabled;
  }

  _onButtonClick() {
    this._sendMessage();
  }

  _onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this._sendMessage();
    }
  }

  _sendMessage() {
    const text = this._input.value.trim();
    if (text && !this.hasAttribute("disabled")) {
      this.dispatchEvent(new CustomEvent("message-send", {
        detail: { text },
        bubbles: true,
        composed: true
      }));
      this._input.value = "";
    }
  }

  // Public API
  get value() {
    return this._input.value;
  }

  set value(val) {
    this._input.value = val;
  }

  focus() {
    this._input.focus();
  }

  clear() {
    this._input.value = "";
  }
}

if (!customElements.get("ctl-chat-input")) {
  customElements.define("ctl-chat-input", CtlChatInput);
}

export default CtlChatInput;
